/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, ReactNode} from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio, {RadioProps} from '@material-ui/core/Radio';
import {makeStyles, withTheme} from '@material-ui/core/styles';
import {ClassNameMap} from '@material-ui/styles';
import classnames from 'classnames';
import theme, {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface RadioTypes extends RadioProps {
  label: string | ReactNode;
  labelClasses?: Partial<ClassNameMap<string>>
}

const useStyles = makeStyles(theme => ({
  // Look like a new 2px border will create another wrapper around the component.
  focus : {
    '&.Mui-focusVisible' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    }
  },
  root : {
    '&:hover' : {
      backgroundColor : dlsAlt('components', 'radio', 'hoverColor')
    }
  },
  radio : {
    '&$checked' : {
      color : dlsColor('caBlue')
    }
  },
  checked : {}
}));

/**
 * @RadioButton
 *
 * Defines the RadioButton component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *   const [selectedValue, setSelectedValue] = useState('male');
 *
 *   const handleChange = function(event) {
 *     setSelectedValue(event.target.value);
 *   };
 *   <RadioGroup name="">
 *     {controls.map((object,index) =>
 *      <RadioButton key={index} onChange={handleChange}
 *        label={object.label}
 *        disabled={object.disabled}
 *        value={object.value} />
 *     )}
 *   </RadioGroup>
 *
 * ```
 */
export const RadioButton = forwardRef(({label, labelClasses, ...props}: RadioTypes, ref) => {
  const classes = useStyles();

  return (
    <FormControlLabel label={label}
      classes={labelClasses}
      control={
        <Radio className={classnames(classes.root, classes.focus)}
          classes={{root: classes.radio, checked: classes.checked}}
          disableFocusRipple
          color='primary'
          ref={ref}
          {...props} />
      } />
  );
});

export default withTheme(RadioButton);
